/**
 * Find In Store: drawer integration.
 *
 * Owns the storefront-side flow for the `product-find-in-store` block:
 *
 *   1. Google Places Autocomplete dropdown beneath the postcode input
 *      (debounced, session-tokened).
 *   2. Google Maps Geocoding (place_id → coords, or raw text → coords).
 *   3. Browser geolocation as an alternative coord source.
 *   4. Storefront API `storeAvailability` fetch per variant, sorted by
 *      `near` and filtered by the block's `excluded_location_ids`.
 *   5. Traffic-light derivation per location for the stock badge.
 *   6. `Set as your store` persistence (localStorage + cart attribute).
 *
 * Runtime architecture :
 *
 *   - Alpine.data('findInStore', …) — per-block factory. Owns the
 *     drawer-scoped reactive UI state (input value, suggestions list,
 *     active-descendant index, results array, current `state`). All HTML
 *     for suggestions and result cards is rendered declaratively in the
 *     block's Liquid template via `<template x-for>` + `x-show`.
 *
 *   - Alpine.store('findInStore', …) — shared cross-component truth.
 *     Owns `preferredStore` (synced to localStorage), `placesUnavailable`
 *     (sticky once a Places call 4xx's), and `currentVariantId` (set by
 *     `variant:change` listeners, consumed by pass 2/3). The store is
 *     register-once-per-page — first block to mount registers it.
 *
 * The factory publishes `fis:store-set` on `document` when a customer
 * picks a store; the filter-bar trigger (and follow-up build passes)
 * listen on the same event.
 */
(function () {
  const STORAGE = {
    geocodeCache: 'eckerleys:geocode_cache',
    searchCoords: 'eckerleys:search_coords',
    preferredStore: 'eckerleys:preferred_store',
  };
  const CART_ATTRIBUTE = '_preferred_store_id';
  const MIN_AUTOCOMPLETE_CHARS = 2;
  const MAX_SUGGESTIONS = 5;
  // Same-origin App Proxy fronting the app's Admin-API-backed locations reads.
  // The storefront path is /apps/proxy/*; Shopify signs and forwards to the
  // Cloudflare worker, which authenticates the signature and calls the Admin
  // API. Storefront `storeAvailability` was gated on local pickup being on,
  // so any location without pickup was invisible - this proxy returns every
  // active location and flags pickup separately.
  const PROXY_BASE = '/apps/proxy/fis';
  const PROXY_MAX_LOCATIONS = 100;
  const PLACES_BASE = 'https://places.googleapis.com/v1/places:autocomplete';
  const PLACES_SEARCH_TEXT_BASE = 'https://places.googleapis.com/v1/places:searchText';
  const PLACES_DETAILS_BASE = 'https://places.googleapis.com/v1/places/';
  const PLACES_FIELD_MASK = 'suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat';
  const PLACES_DETAILS_FIELD_MASK = 'location,formattedAddress';
  const PLACES_SEARCH_TEXT_FIELD_MASK = 'places.location,places.formattedAddress';

  // ------------------------------------------------------------------
  //  Pure helpers (no DOM, no Alpine)
  // ------------------------------------------------------------------

  function readSearchCoords() {
    try {
      const raw = sessionStorage.getItem(STORAGE.searchCoords);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  }

  function writeSearchCoords(payload) {
    try {
      sessionStorage.setItem(STORAGE.searchCoords, JSON.stringify(payload));
    } catch (err) {
      /* storage quota / disabled: non-fatal */
    }
  }

  function readPreferredStore() {
    try {
      const raw = localStorage.getItem(STORAGE.preferredStore);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  }

  function writePreferredStore(payload) {
    try {
      localStorage.setItem(STORAGE.preferredStore, JSON.stringify(payload));
    } catch (err) {
      /* non-fatal */
    }
  }

  function readGeocodeCache() {
    try {
      const raw = sessionStorage.getItem(STORAGE.geocodeCache);
      return raw ? JSON.parse(raw) : {};
    } catch (err) {
      return {};
    }
  }

  function writeGeocodeCache(cache) {
    try {
      sessionStorage.setItem(STORAGE.geocodeCache, JSON.stringify(cache));
    } catch (err) {
      /* non-fatal */
    }
  }

  function normaliseQuery(value) {
    return (value || '').toString().trim().toLowerCase();
  }

  function newSessionToken() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'fis-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  function variantGid(numericId) {
    return 'gid://shopify/ProductVariant/' + numericId;
  }

  function lookupEmbargo(map, variantId) {
    if (!map || variantId == null) return false;
    return !!map[String(variantId)];
  }

  function locationGidToNumeric(gid) {
    const match = String(gid || '').match(/Location\/(\d+)/);
    return match ? match[1] : null;
  }

  function distanceKm(originLat, originLon, locLat, locLon) {
    if (
      originLat == null ||
      originLon == null ||
      locLat == null ||
      locLon == null
    ) {
      return null;
    }
    const R = 6371;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(locLat - originLat);
    const dLon = toRad(locLon - originLon);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(originLat)) *
        Math.cos(toRad(locLat)) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatDistanceKm(km) {
    if (km == null || Number.isNaN(km)) return '';
    if (km < 10) return km.toFixed(1) + ' km';
    return Math.round(km) + ' km';
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (ch) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[ch]);
  }

  function resolveI18nKey(i18n, key) {
    if (!i18n) return null;
    const parts = key.split('.');
    let cursor = i18n;
    for (let i = 0; i < parts.length; i += 1) {
      if (cursor && Object.prototype.hasOwnProperty.call(cursor, parts[i])) {
        cursor = cursor[parts[i]];
      } else {
        return null;
      }
    }
    return typeof cursor === 'string' ? cursor : null;
  }

  function tTranslate(i18n, key, replacements) {
    // The locale lookup is rooted under `products.find_in_store.*` in
    // Shopify's locale file, but the Liquid-side payload script omits
    // that prefix to keep the JSON tight. Accept both shapes here.
    const shortKey = key.indexOf('products.find_in_store.') === 0
      ? key.slice('products.find_in_store.'.length)
      : key;
    const fallback = resolveI18nKey(i18n, shortKey) || key;
    if (!replacements) return fallback;
    return fallback.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) =>
      replacements[name] != null ? replacements[name] : ''
    );
  }

  // ------------------------------------------------------------------
  //  Traffic-light derivation
  // ------------------------------------------------------------------

  function badgeForAvailability(node, config) {
    const quantityAvailable = node.quantityAvailable;
    const available = node.available;
    if (quantityAvailable == null) {
      if (available === false) return { level: 'out' };
      return { level: 'limited' };
    }
    const adjusted = Math.max(0, quantityAvailable - config.stockBuffer);
    if (adjusted === 0) return { level: 'out' };
    if (adjusted >= config.highThreshold) return { level: 'high' };
    if (adjusted >= config.lowThreshold) return { level: 'limited' };
    return { level: 'low', count: adjusted };
  }

  function badgeRangeLabel(config) {
    const low = Math.max(1, config.lowThreshold - 1);
    if (config.lowThreshold <= 2) return '1';
    if (config.lowThreshold > 11) return ''; // bracket gets noisy: drop it
    return '1-' + low;
  }

  function badgeLabel(badge, config) {
    if (badge.level === 'high') return tTranslate(config.i18n, 'stock.high');
    if (badge.level === 'limited') return tTranslate(config.i18n, 'stock.limited');
    if (badge.level === 'out') return tTranslate(config.i18n, 'stock.out');
    if (badge.level === 'low') {
      const range = badgeRangeLabel(config);
      const rendered = range || String(badge.count != null ? badge.count : '');
      return tTranslate(config.i18n, 'stock.low', { range: rendered });
    }
    return '';
  }

  // Adapter for the proxy shape: the drawer speaks in `{ inventory: { quantityAvailable } }`
  // objects; `badgeForAvailability` still speaks in the Storefront shape. This
  // bridge keeps `badgeForAvailability` untouched (it's also fed by other
  // paths) and lets every callsite pass a simple integer.
  function badgeForQuantity(quantityAvailable, config) {
    const qty = quantityAvailable == null ? null : Number(quantityAvailable);
    return badgeForAvailability(
      { quantityAvailable: qty, available: qty != null && qty > 0 },
      config
    );
  }

  // ------------------------------------------------------------------
  //  Google Places + geocode
  // ------------------------------------------------------------------

  function placesAutocomplete(query, sessionToken, config) {
    if (!config.googleMapsKey) return Promise.resolve([]);
    return fetch(PLACES_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': config.googleMapsKey,
        'X-Goog-FieldMask': PLACES_FIELD_MASK,
      },
      body: JSON.stringify({
        input: query,
        languageCode: 'en',
        regionCode: 'AU',
        includedRegionCodes: ['AU'],
        sessionToken,
      }),
    }).then((res) => {
      if (!res.ok) {
        const err = new Error('places_unavailable');
        err.status = res.status;
        throw err;
      }
      return res.json();
    }).then((payload) => {
      const suggestions = (payload && payload.suggestions) || [];
      return suggestions
        .map((suggestion) => {
          const prediction = suggestion.placePrediction;
          if (!prediction) return null;
          return {
            placeId: prediction.placeId,
            description: (prediction.text && prediction.text.text) || '',
            mainText: prediction.structuredFormat &&
              prediction.structuredFormat.mainText &&
              prediction.structuredFormat.mainText.text,
            secondaryText: prediction.structuredFormat &&
              prediction.structuredFormat.secondaryText &&
              prediction.structuredFormat.secondaryText.text,
            matches: (prediction.text && prediction.text.matches) || [],
          };
        })
        .filter(Boolean)
        .slice(0, MAX_SUGGESTIONS);
    });
  }

  function placeDetails(placeId, sessionToken, config) {
    if (!config.googleMapsKey || !placeId) return Promise.resolve(null);
    return fetch(PLACES_DETAILS_BASE + encodeURIComponent(placeId) + '?sessionToken=' + encodeURIComponent(sessionToken), {
      method: 'GET',
      headers: {
        'X-Goog-Api-Key': config.googleMapsKey,
        'X-Goog-FieldMask': PLACES_DETAILS_FIELD_MASK,
      },
    }).then((res) => {
      if (!res.ok) {
        const err = new Error('place_details_failed');
        err.status = res.status;
        throw err;
      }
      return res.json();
    }).then((payload) => {
      if (!payload || !payload.location) return null;
      return {
        latitude: payload.location.latitude,
        longitude: payload.location.longitude,
      };
    });
  }

  function geocode(query, config) {
    if (!config.googleMapsKey) return Promise.resolve(null);
    const normalised = normaliseQuery(query);
    if (!normalised) return Promise.resolve(null);
    const cache = readGeocodeCache();
    if (cache[normalised]) return Promise.resolve(cache[normalised]);
    return fetch(PLACES_SEARCH_TEXT_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': config.googleMapsKey,
        'X-Goog-FieldMask': PLACES_SEARCH_TEXT_FIELD_MASK,
      },
      body: JSON.stringify({
        textQuery: query,
        languageCode: 'en',
        regionCode: 'AU',
      }),
    })
      .then((res) => {
        if (!res.ok) {
          const err = new Error('geocode_failed');
          err.status = res.status;
          throw err;
        }
        return res.json();
      })
      .then((payload) => {
        const place = payload && payload.places && payload.places[0];
        if (!place || !place.location) return null;
        const coords = {
          latitude: place.location.latitude,
          longitude: place.location.longitude,
          formattedAddress: place.formattedAddress || '',
        };
        cache[normalised] = coords;
        writeGeocodeCache(cache);
        return coords;
      })
      .catch(() => null);
  }

  // ------------------------------------------------------------------
  //  Admin-API proxy (App Proxy at /apps/proxy/fis/*)
  //
  //  The proxy returns `FisLocation[]` (see functions/admin/app/utils/
  //  fis.server.ts). Shape: { id, locationId, name, address, pickup: {
  //  enabled, pickupTime, instructions }, inventory: { quantityAvailable,
  //  tracked }, distanceKm }. `id` is the GID and `locationId` the numeric
  //  form; both are ready-made so callers pick whichever the downstream
  //  markup needs. When `near` is supplied, results arrive pre-sorted
  //  ascending by `distanceKm` (haversine, server-side).
  // ------------------------------------------------------------------

  function fetchStoreAvailability(variantId, coords, config) {
    if (!variantId) return Promise.resolve([]);
    const params = new URLSearchParams();
    params.set('variant_id', String(variantId));
    params.set('first', String(PROXY_MAX_LOCATIONS));
    if (coords && coords.latitude != null && coords.longitude != null) {
      params.set('near', coords.latitude + ',' + coords.longitude);
    }
    // `credentials: 'same-origin'` lets Shopify attach the customer session
    // cookie that carries `logged_in_customer_id` to the proxy - not needed
    // for locations today but future FIS extensions (per-customer stock,
    // saved-stores) may want it, and the header is same-origin either way.
    return fetch(PROXY_BASE + '/locations?' + params.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((res) => {
      if (!res.ok) throw new Error('fis_proxy_http_' + res.status);
      return res.json();
    }).then((payload) => {
      return (payload && Array.isArray(payload.locations)) ? payload.locations : [];
    });
  }

  function filterExcluded(locations, excludedIds) {
    if (!excludedIds.length) return locations;
    return locations.filter((location) => !excludedIds.includes(location.locationId));
  }

  // ------------------------------------------------------------------
  //  Cart attribute sync
  // ------------------------------------------------------------------

  function syncCartAttribute(numericId) {
    if (!numericId) return Promise.resolve(null);
    const formData = new FormData();
    formData.append('attributes[' + CART_ATTRIBUTE + ']', numericId);
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: formData,
    })
      .then((res) => res.json())
      .catch(() => null);
  }

  // ------------------------------------------------------------------
  //  Highlight matches (used for suggestion HTML via x-html)
  //
  //  Input is Places API prediction text + match offsets — server-side
  //  data we already escape before insertion. Bolded matched ranges
  //  ship as a small HTML string the template renders with x-html.
  // ------------------------------------------------------------------

  function highlightMatches(text, matches) {
    if (!matches || !matches.length) return escapeHtml(text);
    const sorted = matches
      .map((m) => ({
        start: m.startOffset != null ? m.startOffset : 0,
        end: m.endOffset != null ? m.endOffset : 0,
      }))
      .sort((a, b) => a.start - b.start);
    let out = '';
    let cursor = 0;
    sorted.forEach((range) => {
      if (range.start > cursor) {
        out += escapeHtml(text.slice(cursor, range.start));
      }
      out += '<strong>' + escapeHtml(text.slice(range.start, range.end)) + '</strong>';
      cursor = range.end;
    });
    if (cursor < text.length) out += escapeHtml(text.slice(cursor));
    return out;
  }

  // ------------------------------------------------------------------
  //  Alpine wiring
  // ------------------------------------------------------------------

  // In-flight Storefront fetches keyed `${variantId}:${locationId}` so
  // rapid variant:change events collapse to one network round-trip per
  // unique (variant, store) pair. Module-scoped — survives across both
  // the factory and the trigger Alpine roots.
  const stockInFlight = new Map();

  // Module-scoped pointer to the live config (storefront token,
  // thresholds, etc.). The Alpine factory writes this on `init` so
  // document-level event listeners can call `fetchStockAtPreferredStore`
  // without having to chase `this.config` through proxy contexts that
  // sometimes lose their binding.
  let liveConfig = null;

  function fetchStockAtPreferredStore(variantId, storeData, config) {
    if (!variantId || !storeData || !storeData.locationId) {
      return Promise.resolve(null);
    }
    const key = variantId + ':' + storeData.locationId;
    if (stockInFlight.has(key)) return stockInFlight.get(key);

    // Dedicated single-row endpoint - see functions/admin/app/routes/
    // apps.proxy.fis.variant-stock.tsx. Cheaper than reusing the drawer's
    // all-locations query when the answer is always one variant × one
    // location; cost stays flat as the merchant's location count grows.
    const numericVariantId = String(variantId).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    const numericLocationId = locationGidToNumeric(storeData.locationId) || storeData.locationId;

    const params = new URLSearchParams();
    params.set('variant_id', numericVariantId);
    params.set('location_id', String(numericLocationId));

    const promise = fetch(PROXY_BASE + '/variant-stock?' + params.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('preferred_stock_http_' + res.status)))
      .then((payload) => {
        const qty = payload && payload.variant ? payload.variant.quantityAvailable : null;
        if (qty == null) return { level: 'out' };
        return badgeForQuantity(qty, config);
      })
      .catch(() => null)
      .finally(() => stockInFlight.delete(key));
    stockInFlight.set(key, promise);
    return promise;
  }

  // ─── Grid: batched per-product stock query ───────────────────────
  //
  //   Single App-Proxy round-trip pulls `available` for every variant
  //   on a product at the preferred store. Returns a
  //   Map<variantGid, badge> keyed for fast per-card lookup. Cached
  //   in-flight by `${productId}:${locationId}` so multiple Alpine
  //   roots ("cardStockProbe" instances) on the same page collapse
  //   to one fetch. Variants excluded via the `custom.find_in_store_
  //   excluded` metafield are suppressed server-side by the grid
  //   template's `{% unless %}` guard; the client no longer mirrors
  //   the check.

  const productStockInFlight = new Map();

  function fetchProductStockMap(productId, storeData, config) {
    if (!productId || !storeData || !storeData.locationId || !config) {
      return Promise.resolve(null);
    }
    const numericProductId = String(productId).replace(/^gid:\/\/shopify\/Product\//, '');
    const numericLocationId = locationGidToNumeric(storeData.locationId) || storeData.locationId;
    const key = numericProductId + ':' + numericLocationId;
    if (productStockInFlight.has(key)) return productStockInFlight.get(key);

    const params = new URLSearchParams();
    params.set('product_id', numericProductId);
    params.set('location_id', String(numericLocationId));

    const promise = fetch(PROXY_BASE + '/product-stock?' + params.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('grid_stock_http_' + res.status)))
      .then((payload) => {
        const variants = (payload && Array.isArray(payload.variants)) ? payload.variants : [];
        const map = {};
        variants.forEach((variant) => {
          if (!variant.id) return;
          map[variant.id] = badgeForQuantity(variant.quantityAvailable, config);
        });
        return map;
      })
      .catch(() => null)
      .finally(() => productStockInFlight.delete(key));
    productStockInFlight.set(key, promise);
    return promise;
  }

  function registerSharedStore(config, i18n) {
    if (!window.Alpine || window.Alpine.store('findInStore')) return;
    window.Alpine.store('findInStore', {
      preferredStore: readPreferredStore(),
      placesUnavailable: false,
      isEmbargoed: false,           // per-variant `custom.pickup_excluded` for the CURRENTLY selected variant — seeded at init, kept live by `updateEmbargoForCurrentVariant()` on drawer:open + variant:change
      currentVariantId: null,
      currentBadge: null,           // { level, count? } — selected variant @ preferred store
      cardStockByVariant: {},       // { [variantGid]: { level, count? } } — grid template
      hasExplicitVariant: false,    // true once the customer has user-confirmed a variant
                                    //   (either via ?variant= URL or via picker interaction)
      config,                       // thresholds, tokens, etc. — set once by first block
      i18n,                         // translations — set once by first block

      setPreferredStore(payload) {
        // Pure state mutation. Stock-refetch is the factory's job — it
        // owns the live `config` (token + thresholds) and listens for
        // the `fis:store-set` event we dispatch below.
        this.preferredStore = payload;
        if (payload) {
          writePreferredStore(payload);
          const numeric = locationGidToNumeric(payload.locationId);
          if (numeric) syncCartAttribute(numeric);
        }
        if (!payload) {
          this.currentBadge = null;
          this.cardStockByVariant = {};
        }
        document.dispatchEvent(
          new CustomEvent('fis:store-set', { detail: payload })
        );
      },

      setCurrentBadge(badge) {
        this.currentBadge = badge;
      },

      setCurrentVariantId(variantId) {
        // Pure state mutation. Like `setPreferredStore`, defer the
        // stock-refetch to the factory's `variant:change` listener so
        // we never end up calling `fetchStoreAvailability` without a
        // live `config` reference.
        if (this.currentVariantId === variantId) return;
        this.currentVariantId = variantId;
        if (!variantId) this.currentBadge = null;
      },

      setCardStockByVariant(map) {
        this.cardStockByVariant = map || {};
      },

      // ─── Computed: drives both inline CTA + filter-bar trigger ─────
      //   Reads `currentBadge` (per-store + per-variant stock level),
      //   `preferredStore.name`, `currentVariantId`. Both Alpine roots
      //   bind `x-text` to these.
      get ctaBadgeLevel() {
        // No explicit variant → suppress the traffic-light so the dot
        // falls back to the default grey, matching the "Select options"
        // copy state.
        if (!this.hasExplicitVariant) return null;
        // Embargoed variant → warning dot, matching every drawer row
        // (normaliseCard forces level 'limited' when isEmbargoed).
        if (this.isEmbargoed) return 'limited';
        return this.currentBadge && this.currentBadge.level ? this.currentBadge.level : null;
      },

      get ctaLabel() {
        const store = this.preferredStore;
        const badge = this.currentBadge;
        if (this.hasExplicitVariant && this.isEmbargoed) {
          const embargoMsg = tTranslate(this.i18n, 'embargo_label');
          return store ? embargoMsg + ': ' + store.name : embargoMsg;
        }
        if (!store) return tTranslate(this.i18n, 'label');
        // Store is set but the customer hasn't user-confirmed a variant
        // (multi-option product, no `?variant=` in URL, no picker
        // interaction yet) → prompt them to pick.
        if (!this.hasExplicitVariant) {
          return tTranslate(this.i18n, 'select_options_label');
        }
        if (!this.currentVariantId || !badge) {
          return tTranslate(this.i18n, 'label');
        }
        const stockMessage = ctaStockLabel(badge, this.config, this.i18n);
        return stockMessage + ': ' + store.name;
      },

      get ctaHint() {
        const store = this.preferredStore;
        const badge = this.currentBadge;
        if (!store) return tTranslate(this.i18n, 'placeholder');
        // When the customer hasn't user-confirmed a variant we surface
        // the store name as a hint so it stays visible alongside the
        // "Select options" primary label.
        if (!this.hasExplicitVariant) return tTranslate(this.i18n, 'change_store');
        if (!this.currentVariantId) return tTranslate(this.i18n, 'select_option');
        if (this.isEmbargoed) return tTranslate(this.i18n, 'change_store');
        if (!badge) return tTranslate(this.i18n, 'loading');
        return tTranslate(this.i18n, 'change_store');
      },
    });
  }

  // CTA-specific stock label: same shape as the drawer's `badgeLabel`
  // but reads from `stock_cta.*` instead of `stock.*` so the high band
  // can read "In Stock" while the drawer keeps "High Stock".
  function ctaStockLabel(badge, config, i18n) {
    if (!badge || !badge.level) return '';
    if (badge.level === 'high')    return tTranslate(i18n, 'stock_cta.high');
    if (badge.level === 'limited') return tTranslate(i18n, 'stock_cta.limited');
    if (badge.level === 'out')     return tTranslate(i18n, 'stock_cta.out');
    if (badge.level === 'low') {
      const range = badgeRangeLabel(config);
      const rendered = range || String(badge.count != null ? badge.count : '');
      return tTranslate(i18n, 'stock_cta.low', { range: rendered });
    }
    return '';
  }

  document.addEventListener('alpine:init', () => {
    // Register a placeholder store before any factory runs so child
    // triggers (filter-bar) can read $store.findInStore safely on
    // their first render. The factory below upgrades it with real
    // config + i18n once a block instance hydrates.
    registerSharedStore({}, {});

    // Per-card stock probe used by `product-colour-variants.liquid` grid
    // cards. Each card hydrates with its own variant GID and reads from
    // `$store.findInStore.cardStockByVariant`. Pure read scope — no
    // network calls, no state mutation. The main factory's
    // `refreshGridStockMap()` is the singular writer.
    window.Alpine.data('cardStockProbe', (variantGid) => ({
      variantGid,
      get isEmbargoed() {
        const cfg = this.$store.findInStore.config;
        const numeric = String(this.variantGid).match(/ProductVariant\/(\d+)/);
        return lookupEmbargo(cfg && cfg.embargoMap, numeric ? numeric[1] : null);
      },
      get badge() {
        const map = this.$store.findInStore.cardStockByVariant;
        return (map && map[this.variantGid]) || null;
      },
      get level() {
        if (this.isEmbargoed) return 'limited';
        return this.badge ? this.badge.level : null;
      },
      get label() {
        if (this.isEmbargoed) {
          const storeName = this.$store.findInStore.preferredStore
            && this.$store.findInStore.preferredStore.name;
          const embargoMsg = tTranslate(this.$store.findInStore.i18n, 'embargo_label');
          return storeName ? embargoMsg + ' in ' + storeName : embargoMsg;
        }
        const b = this.badge;
        if (!b) return '';
        const i18n = this.$store.findInStore.i18n;
        const storeName = this.$store.findInStore.preferredStore
          && this.$store.findInStore.preferredStore.name;
        let stockMessage = '';
        if (b.level === 'high')    stockMessage = tTranslate(i18n, 'stock_cta.high');
        else if (b.level === 'limited') stockMessage = tTranslate(i18n, 'stock_cta.limited');
        else if (b.level === 'out')     stockMessage = tTranslate(i18n, 'stock_cta.out');
        else if (b.level === 'low')     stockMessage = tTranslate(i18n, 'stock_cta.low', { range: '' });
        if (!stockMessage) return '';
        // Card label is level-only — strip any bracketed threshold helpers
        // baked into the i18n template (e.g. "Low Stock [{{ range }}]" or
        // "Low Stock [1-3]"). Brackets are logic for the PDP CTA, not for
        // the card surface.
        stockMessage = stockMessage.replace(/\s*\[[^\]]*\]\s*/g, ' ').replace(/\s+/g, ' ').trim();
        // Append the preferred store name so each card reads
        // "{stock_message} in {store_name}" (e.g. "Limited Stock in Bondi").
        return storeName ? stockMessage + ' in ' + storeName : stockMessage;
      },
    }));

    window.Alpine.data('findInStoreTrigger', () => ({
      // Lightweight Alpine root used by the filter-bar trigger in
      // `product-colour-variants.liquid`. The filter-bar surfaces a
      // **store-only** label (`Your Store: {store_name}`) — separate
      // from the inline PDP CTA which surfaces stock context for the
      // currently-selected variant. On grid view the customer hasn't
      // chosen a variant yet, so per-variant traffic-light state isn't
      // meaningful here. The per-card stock labels (pass 3) carry that
      // responsibility instead.
      get triggerLabel() {
        const store = this.$store.findInStore.preferredStore;
        const i18n = this.$store.findInStore.i18n;
        if (!store) return tTranslate(i18n, 'label');
        return tTranslate(i18n, 'your_store') + ': ' + store.name;
      },
      get triggerHint() {
        const store = this.$store.findInStore.preferredStore;
        const i18n = this.$store.findInStore.i18n;
        if (!store) return tTranslate(i18n, 'placeholder');
        return tTranslate(i18n, 'change_store');
      },
      get triggerBadgeLevel() {
        // Static `high` (green) once a store is selected. Filter-bar
        // doesn't reflect per-variant traffic-light — it's a "store is
        // set" affordance, not a stock indicator.
        return this.$store.findInStore.preferredStore ? 'high' : null;
      },
    }));

    window.Alpine.data('findInStore', () => ({
      // ─── Config + i18n (read from DOM at init) ─────────────────────
      config: {
        productId: null,
        productHandle: null,
        variantId: null,
        shopDomain: null,
        storefrontToken: '',
        googleMapsKey: '',
        stockBuffer: 0,
        highThreshold: 10,
        lowThreshold: 3,
        excludedIds: [],
        drawerId: '',
        i18n: {},
      },

      // ─── State machine ─────────────────────────────────────────────
      state: 'default',

      // ─── Input + suggestions ───────────────────────────────────────
      inputValue: '',
      suggestions: [],
      suggestionsOpen: false,
      activeIndex: -1,
      sessionToken: '',
      inputDebounceTimer: null,

      // ─── Results ───────────────────────────────────────────────────
      results: [],
      lastCoords: null,
      productPayload: null,

      // ─── Computed ──────────────────────────────────────────────────
      get hasValue() {
        return this.inputValue.trim().length > 0;
      },

      get preferredStore() {
        return this.$store.findInStore && this.$store.findInStore.preferredStore;
      },

      get preferredLocationId() {
        return this.preferredStore ? this.preferredStore.locationId : null;
      },

      get isGridNoVariant() {
        return !!(this.config && this.config.isGridNoVariant);
      },

      // ─── Reactive summary lookups ──────────────────────────────────
      //   The drawer's product summary row (thumb + variant subtitle)
      //   needs to reflect the customer's currently-chosen variant.
      //   These getters look up the variant in the cached product JSON
      //   (`productPayload`) using the live `currentVariantId` from the
      //   shared store; both Alpine reactivity sources update when the
      //   customer changes a variant via the PDP form.
      get currentVariant() {
        const payload = this.productPayload;
        const variantId = this.$store.findInStore && this.$store.findInStore.currentVariantId;
        if (!payload || !payload.variants || !variantId) return null;
        const id = String(variantId);
        return payload.variants.find((variant) => String(variant.id) === id) || null;
      },

      get currentVariantTitle() {
        const variant = this.currentVariant;
        if (!variant) return '';
        // Hide the "Default Title" placeholder Shopify emits when the
        // product has no real options — same guard as the Liquid render.
        if (variant.title === 'Default Title') return '';
        return variant.title || '';
      },

      get currentVariantImage() {
        const variant = this.currentVariant;
        const featured = variant && variant.featured_image && variant.featured_image.src;
        if (featured) return featured;
        // Fall back to the product's primary image which the Liquid
        // template already seeded into the markup.
        const payload = this.productPayload;
        if (payload && payload.featured_image) return payload.featured_image;
        return null;
      },

      // ─── Lifecycle ─────────────────────────────────────────────────
      init() {
        const root = this.$root;
        this.config = this.readConfig(root);
        // Mirror onto the module-scoped pointer so document-level
        // listeners can resolve the storefront token without chasing
        // `this.config` through proxy contexts.
        liveConfig = this.config;

        // Only Google Maps is required client-side now; locations + stock
        // come from the same-origin App Proxy, which needs no token here.
        if (!this.config.googleMapsKey) {
          this.state = 'config_missing';
          return;
        }

        this.sessionToken = newSessionToken();

        // Hydrate from persisted coords on first init so re-opens are instant.
        const persistedCoords = readSearchCoords();
        if (persistedCoords) this.lastCoords = persistedCoords;

        // If a preferred store is saved but the cart doesn't reflect it, re-apply.
        const preferred = this.$store.findInStore.preferredStore;
        if (preferred && preferred.locationId) {
          const numeric = locationGidToNumeric(preferred.locationId);
          if (numeric) syncCartAttribute(numeric); // fire-and-forget
        }

        // First factory instance to mount upgrades the placeholder
        // store with real config + i18n. Subsequent blocks on the same
        // page no-op (store is already populated).
        const sharedStore = this.$store.findInStore;
        if (!sharedStore.config || !sharedStore.config.googleMapsKey) {
          sharedStore.config = this.config;
          sharedStore.i18n = this.config.i18n;
        }

        // Hydrate the product JSON via the shared `loadProductJson`
        // accessor on `window.theme` (exposed by `product-form.js`).
        // The cache is shared with the PDP form so opening the drawer
        // never re-fetches `/products/{handle}.js`.
        if (window.theme && typeof window.theme.loadProductJson === 'function' && this.config.productHandle) {
          window.theme.loadProductJson(this.config.productHandle).then((payload) => {
            this.productPayload = payload;
          });
        }

        // Seed the current variant on the shared store. Initial stock
        // fetch (if a store is also preferred) is kicked below in the
        // store-set listener — same path used after a user pick.
        sharedStore.setCurrentVariantId(this.config.variantId);

        // `hasExplicitVariant` mirrors Liquid's `product.selected_variant`
        // boolean — true when the page loaded with `?variant=` in the
        // URL. Updated to true on the first user variant pick below.
        sharedStore.hasExplicitVariant = !!this.config.hasExplicitVariant;

        // Embargo (per-variant `custom.pickup_excluded`). Seed a first-
        // paint value from the SSR-rendered `embargoed` flag (current
        // variant at page load) so the drawer's initial render is correct
        // before any drawer-open/variant-change event fires —
        // `normaliseCard()` reads `isEmbargoed` to override every drawer
        // location row with the "contact the store" status.
        sharedStore.isEmbargoed = !!this.config.embargoed;

        // Kick a fetch if both pieces of context are already present
        // on init (returning visit with persisted store). Also
        // hydrate the grid stock map so per-card pills paint on first
        // render without waiting for a store-pick event.
        if (sharedStore.preferredStore && sharedStore.currentVariantId) {
          this.refreshStockBadge();
        }
        if (sharedStore.preferredStore) {
          this.refreshGridStockMap();
        }

        // `fis:store-set` is dispatched by the store every time the
        // customer picks "Set as your store" in the drawer. Re-look-up
        // the live Alpine factory via `Alpine.$data(root)` at fire-time
        // so `config` resolves correctly regardless of closure context.
        document.addEventListener('fis:store-set', () => {
          const factory = window.Alpine.$data(root);
          factory.refreshStockBadge();
          factory.refreshGridStockMap();
        });

        // Hydrate the drawer body on first open if cached coords exist.
        const drawer = document.getElementById(this.config.drawerId);
        if (drawer) {
          drawer.addEventListener('drawer:open', () => {
            this.publishFisClick();
            // Re-evaluate embargo for whatever variant is selected.
            this.updateEmbargoForCurrentVariant();
            if (this.state === 'default' && this.lastCoords) {
              this.state = 'loading';
              this.performStorefrontFetch(this.lastCoords);
            }
          });
        }

        // Listen for variant changes from the PDP form (Alpine
        // `productForm` factory in `product-form.js` dispatches
        // `variant:change` with `{ variant }` on its root, which
        // bubbles to document). Debounce 250ms so colour-card spam
        // (grid template) collapses to one fetch per quiet period.
        let variantDebounceTimer = null;
        document.addEventListener('variant:change', (event) => {
          const variant = event.detail && event.detail.variant;
          if (!variant || !variant.id) return;
          // First user-initiated variant change → flip the explicit flag
          // so the CTA drops out of the "Select options" state.
          sharedStore.hasExplicitVariant = true;
          window.clearTimeout(variantDebounceTimer);
          variantDebounceTimer = window.setTimeout(() => {
            sharedStore.setCurrentVariantId(String(variant.id));
            const factory = window.Alpine.$data(root);
            factory.refreshStockBadge();
            factory.refreshDrawerForVariant();
            factory.updateEmbargoForCurrentVariant();
          }, 250);
        });

        // Close suggestions on outside click.
        document.addEventListener('click', (event) => {
          const form = root.querySelector('[data-product-find-in-store-form]');
          if (form && !form.contains(event.target)) {
            this.closeSuggestions();
          }
        });
      },

      // find_in_store_click. product_id is the parent SKU
      // (custom.source_id); variant_id is the current variant's SKU,
      // resolved from the cached product JSON; store_count is sent only
      // when results are already showing, otherwise omitted.
      publishFisClick() {
        if (!window.theme || typeof window.theme.publish !== 'function') return;
        const root = this.$root;
        const variantId =
          (this.$store.findInStore && this.$store.findInStore.currentVariantId) ||
          this.config.variantId;
        let variantSku = '';
        const payload = this.productPayload;
        if (payload && Array.isArray(payload.variants) && variantId) {
          const match = payload.variants.find((v) => String(v.id) === String(variantId));
          if (match && match.sku) variantSku = match.sku;
        }
        const data = {
          product_id: root.dataset.fisSourceId || '',
          variant_id: variantSku,
        };
        if (this.state === 'with-results' && Array.isArray(this.results)) {
          data.store_count = this.results.length;
        }
        window.theme.publish('eckersleys:find_in_store_click', data);
      },

      readConfig(root) {
        const i18n = this.readI18n(root);
        const embargoMap = (i18n && i18n.embargo_map) || {};
        delete i18n.embargo_map;
        /* Trading hours ride in on the same payload script, keyed by
           Shopify location id. Lifted off `i18n` like `embargo_map` so the
           label map stays label-only for `tTranslate`. */
        const storeHours = (i18n && i18n.store_hours) || {};
        delete i18n.store_hours;
        return {
          productId: root.dataset.fisProductId,
          productHandle: root.dataset.fisProductHandle,
          variantId: root.dataset.fisVariantId,
          shopDomain: root.dataset.fisShopDomain,
          storefrontToken: (root.dataset.fisStorefrontToken || '').trim(),
          googleMapsKey: (root.dataset.fisGoogleMapsKey || '').trim(),
          stockBuffer: parseInt(root.dataset.fisStockBuffer || '0', 10) || 0,
          highThreshold: parseInt(root.dataset.fisHighThreshold || '10', 10) || 10,
          lowThreshold: parseInt(root.dataset.fisLowThreshold || '3', 10) || 3,
          excludedIds: (root.dataset.fisExcludedIds || '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean),
          drawerId: root.dataset.fisDrawerId,
          isGridNoVariant: root.dataset.fisIsGridNoVariant === 'true',
          hasExplicitVariant: root.dataset.fisHasExplicitVariant === 'true',
          embargoed: root.dataset.fisEmbargoed === 'true',
          embargoMap,
          storeHours,
          i18n,
        };
      },

      readI18n(root) {
        const script = root.querySelector('script[data-fis-i18n]');
        if (!script) return {};
        try {
          // Shopify's `| t` HTML-escapes some characters (`&`, `'`, …) when
          // resolving a locale string. The IIFE this factory replaces wrote
          // those strings via `innerHTML`, which silently decoded the
          // entities. `x-text` writes them as literal text, so we decode
          // here so `Click &amp; collect` renders as `Click & collect`.
          return this.decodeEntitiesDeep(JSON.parse(script.textContent || '{}'));
        } catch (err) {
          return {};
        }
      },

      decodeEntitiesDeep(input) {
        if (input == null) return input;
        if (typeof input === 'string') {
          const textarea = document.createElement('textarea');
          textarea.innerHTML = input;
          return textarea.value;
        }
        if (Array.isArray(input)) {
          return input.map((entry) => this.decodeEntitiesDeep(entry));
        }
        if (typeof input === 'object') {
          const out = {};
          Object.keys(input).forEach((key) => {
            out[key] = this.decodeEntitiesDeep(input[key]);
          });
          return out;
        }
        return input;
      },

      // ─── i18n lookup (template-friendly) ───────────────────────────
      t(key, replacements) {
        return tTranslate(this.config.i18n, key, replacements);
      },

      // ─── State-driven message + class for the results pane ────────
      //   Single `<p>` in the template binds to these two getters so the
      //   markup stays declarative without one element per state.
      get stateMessage() {
        switch (this.state) {
          case 'default':                  return this.t('placeholder_results');
          case 'loading':
          case 'geocoding':
          case 'geolocating':              return this.t('loading');
          case 'empty':                    return this.t('empty_no_stores');
          case 'error_geolocation_denied': return this.t('error_geolocation_denied');
          case 'error_geocode_not_found':  return this.t('error_geocode_not_found');
          case 'error_api':                return this.t('error_api');
          case 'config_missing':           return this.t('config_missing');
          default:                         return '';
        }
      },
      get stateMessageClass() {
        switch (this.state) {
          case 'error_geolocation_denied':
          case 'error_geocode_not_found':
          case 'error_api':
          case 'config_missing':
            return 'product-find-in-store__notice';
          default:
            return 'product-find-in-store__placeholder';
        }
      },

      // ─── Suggestions ───────────────────────────────────────────────
      onInput() {
        const value = this.inputValue.trim();
        if (this.$store.findInStore.placesUnavailable) return;
        if (value.length < MIN_AUTOCOMPLETE_CHARS) {
          this.closeSuggestions();
          return;
        }
        placesAutocomplete(value, this.sessionToken, this.config)
          .then((suggestions) => {
            if (!suggestions.length) {
              this.closeSuggestions();
              return;
            }
            this.suggestions = suggestions;
            this.suggestionsOpen = true;
            this.activeIndex = -1;
          })
          .catch(() => {
            this.$store.findInStore.placesUnavailable = true;
            this.closeSuggestions();
          });
      },

      closeSuggestions() {
        this.suggestionsOpen = false;
        this.suggestions = [];
        this.activeIndex = -1;
      },

      moveActive(direction) {
        if (!this.suggestionsOpen || !this.suggestions.length) return;
        const count = this.suggestions.length;
        if (this.activeIndex < 0) {
          this.activeIndex = direction > 0 ? 0 : count - 1;
          return;
        }
        const next = (this.activeIndex + direction + count) % count;
        this.activeIndex = next;
      },

      onEnter() {
        if (this.suggestionsOpen && this.activeIndex >= 0) {
          this.pickSuggestion(this.suggestions[this.activeIndex]);
        } else {
          this.onSubmit();
        }
      },

      onEscape() {
        if (this.suggestionsOpen) {
          this.closeSuggestions();
        }
      },

      highlightedDescription(item) {
        return highlightMatches(item.description, item.matches);
      },

      suggestionId(index) {
        return this.config.drawerId + '-suggestions-' + index;
      },

      pickSuggestion(item) {
        if (!item) return;
        this.inputValue = item.description;
        this.closeSuggestions();
        this.state = 'loading';
        placeDetails(item.placeId, this.sessionToken, this.config)
          .then((coords) => {
            if (!coords) {
              return geocode(item.description, this.config).then((fb) => fb);
            }
            return coords;
          })
          .then((coords) => {
            this.sessionToken = newSessionToken();
            if (!coords) {
              this.state = 'error_geocode_not_found';
              return null;
            }
            this.lastCoords = Object.assign({}, coords, {
              source: 'geocode',
              query: item.description,
            });
            writeSearchCoords(this.lastCoords);
            return this.performStorefrontFetch(this.lastCoords);
          })
          .catch(() => {
            this.state = 'error_api';
          });
      },

      // ─── Form actions ──────────────────────────────────────────────
      onSubmit() {
        const value = this.inputValue.trim();
        if (!value) return;
        this.closeSuggestions();
        this.state = 'geocoding';
        geocode(value, this.config)
          .then((coords) => {
            if (!coords) {
              this.state = 'error_geocode_not_found';
              return null;
            }
            this.lastCoords = Object.assign({}, coords, {
              source: 'geocode',
              query: value,
            });
            writeSearchCoords(this.lastCoords);
            return this.performStorefrontFetch(this.lastCoords);
          })
          .catch(() => {
            this.state = 'error_api';
          });
      },

      onGeolocate() {
        if (!navigator.geolocation) {
          this.state = 'error_geolocation_denied';
          return;
        }
        this.state = 'geolocating';
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              source: 'geolocation',
              query: '',
            };
            this.lastCoords = coords;
            writeSearchCoords(this.lastCoords);
            this.performStorefrontFetch(coords);
          },
          () => {
            this.state = 'error_geolocation_denied';
          },
          { timeout: 7000, maximumAge: 60000, enableHighAccuracy: false }
        );
      },

      onClear() {
        this.inputValue = '';
        this.closeSuggestions();
        this.sessionToken = newSessionToken();
        const input = this.$root.querySelector('[data-product-find-in-store-input]');
        if (input) input.focus();
      },

      // ─── Storefront fetch + normalisation ──────────────────────────
      performStorefrontFetch(coords) {
        // Read the live current variant id from the shared store so
        // a fresh `variant:change` flows through here without needing
        // a separate `refresh` path. Falls back to the initial id
        // declared at block render time.
        const variantId = (this.$store.findInStore && this.$store.findInStore.currentVariantId)
          || this.config.variantId;
        return fetchStoreAvailability(variantId, coords, this.config)
          .then((nodes) => {
            this.reconcilePreferredStore(nodes);
            const filtered = filterExcluded(nodes, this.config.excludedIds);
            if (!filtered.length) {
              this.results = [];
              this.state = 'empty';
              return;
            }
            this.results = filtered.map((node) => this.normaliseCard(node, coords));
            this.state = 'with-results';
          })
          .catch(() => {
            this.state = 'error_api';
          });
      },

      // Self-heal a persisted preferred store whose location GID no
      // longer matches any live location.
      // setPreferredStore dispatches `fis:store-set`, which the factory
      // listens for and uses to refresh the CTA badge from the new GID.
      reconcilePreferredStore(nodes) {
        const store = this.$store.findInStore;
        const preferred = store && store.preferredStore;
        if (!preferred || !preferred.locationId || !Array.isArray(nodes) || !nodes.length) return;
        const matchesById = nodes.some((node) => node.id === preferred.locationId);
        if (matchesById) return;
        const byName = preferred.name
          ? nodes.find((node) => node.name === preferred.name)
          : null;
        if (byName) {
          const address = byName.address || {};
          store.setPreferredStore({
            locationId: byName.id,
            name: byName.name,
            latitude: address.latitude != null ? address.latitude : preferred.latitude,
            longitude: address.longitude != null ? address.longitude : preferred.longitude,
          });
        } else {
          store.setPreferredStore(null);
        }
      },

      // ─── Variant change: refresh drawer body for new variant ───────
      //   Called from the document-level `variant:change` listener.
      //   Re-runs the storeAvailability fetch with the new variant id
      //   so the in-drawer card list reflects the customer's current
      //   selection.
      refreshDrawerForVariant() {
        if (!this.lastCoords) return;
        this.performStorefrontFetch(this.lastCoords);
      },

      normaliseCard(location, originCoords) {
        // `location` is a FisLocation from the proxy. Distance arrives
        // pre-computed when the fetch supplied `near`; fall back to a
        // client-side haversine so the card meta line still renders in
        // the rare case a caller skipped `near` but has origin coords.
        const address = location.address || {};
        const km = location.distanceKm != null
          ? location.distanceKm
          : (originCoords && address.latitude != null && address.longitude != null
              ? distanceKm(originCoords.latitude, originCoords.longitude, address.latitude, address.longitude)
              : null);
        const distanceFmt = formatDistanceKm(km);
        const qty = location.inventory ? location.inventory.quantityAvailable : 0;
        const badge = badgeForQuantity(qty, this.config);
        const addressLine = [address.address1, address.address2, address.city, address.zip]
          .filter(Boolean)
          .join(', ');
        const metaLine = distanceFmt && addressLine
          ? distanceFmt + ' · ' + addressLine
          : (addressLine || distanceFmt);
        const isEmbargoed = this.$store.findInStore.isEmbargoed;
        return {
          locationId: location.locationId,
          locationGid: location.id,
          name: location.name || '',
          distanceKm: km,
          distanceFmt,
          addressLine,
          metaLine,
          hoursLine: this.hoursLineFor(location.locationId),
          phone: address.phone || '',
          latitude: address.latitude != null ? address.latitude : null,
          longitude: address.longitude != null ? address.longitude : null,
          pickUpAvailable: !!(location.pickup && location.pickup.enabled),
          badge: isEmbargoed ? { level: 'limited' } : badge,
          badgeLabel: isEmbargoed
            ? tTranslate(this.$store.findInStore.i18n, 'embargo_label')
            : badgeLabel(badge, this.config),
        };
      },

      // ─── Stock badge refresh ───────────────────────────────────────
      //   Pulls live stock for the current (variant, store) pair and
      //   writes the result to the shared store. Reads `liveConfig`
      //   from module scope so it works from any caller context
      //   (Alpine event handlers, document listeners, store methods).
      refreshStockBadge() {
        const store = this.$store.findInStore;
        const variantId = store.currentVariantId;
        const preferredStore = store.preferredStore;
        if (!variantId || !preferredStore) {
          store.setCurrentBadge(null);
          return;
        }
        fetchStockAtPreferredStore(variantId, preferredStore, liveConfig)
          .then((badge) => store.setCurrentBadge(badge));
      },

      // ─── Grid stock map refresh ────────────────────────────────────
      //   On the grid template, pull every variant's stock at the
      //   preferred store in a single Storefront round-trip and write
      //   the resulting Map<variantGid, badge> to the shared store.
      //   Per-card Alpine roots (`cardStockProbe`) read reactively.
      refreshGridStockMap() {
        const store = this.$store.findInStore;
        const preferredStore = store.preferredStore;
        const productId = this.config && this.config.productId;
        if (!productId || !preferredStore) {
          store.setCardStockByVariant({});
          return;
        }
        fetchProductStockMap(productId, preferredStore, liveConfig)
          .then((map) => store.setCardStockByVariant(map));
      },

      updateEmbargoForCurrentVariant() {
        const store = this.$store.findInStore;
        store.isEmbargoed = lookupEmbargo(this.config.embargoMap, store.currentVariantId);
      },

      // ─── Set as your store ─────────────────────────────────────────
      setStore(card) {
        const payload = {
          locationId: card.locationGid,
          name: card.name,
          latitude: card.latitude,
          longitude: card.longitude,
        };
        this.$store.findInStore.setPreferredStore(payload);
      },

      // ─── Hours line ────────────────────────────────────────────────
      //   Per STORE, not per component: each store keeps its own trading
      //   hours and its own clock, so one value for the whole drawer is
      //   wrong by construction (that was the #1043 bug).
      //
      //   Called ONCE per store from the card mapper, which stores the
      //   result as `card.hoursLine` alongside `metaLine` / `phone`.
      //   Binding this method straight to `x-show` + `x-text` instead
      //   would recompute it twice per card on every render pass.
      //
      //   `config.storeHours` is keyed by Shopify location id and carries
      //   the full week plus the store's state code (see the map built in
      //   `blocks/product-find-in-store.liquid`); `zoneForState` turns that
      //   into the IANA zone.
      //
      //   Returns '' for any store with no hours on record -- the
      //   warehouses have no `store_location` twin -- and the template
      //   drops the line via `x-show` rather than rendering an empty <p>.
      hoursLineFor(locationId) {
        const record = this.config.storeHours[String(locationId)];
        if (!record || !record.week) return '';

        const { day, time } = window.theme.nowInZone(window.theme.zoneForState(record.state));
        const slot = record.week[day];
        if (!slot || slot.closed) return this.t('hours_closed_today');
        if (!slot.open || !slot.close) return '';

        /* Zero-padded 'HH:MM' compares correctly as a string, so the
           trading-state test needs no parsing. The Places sync truncates
           midnight-spanning periods to 23:59, so `close > open` holds. */
        const isOpen = time >= slot.open && time < slot.close;
        const label = isOpen ? 'hours_open_closes' : 'hours_closed_opens';
        const boundary = isOpen ? slot.close : slot.open;
        /* Global replace: a translation may repeat the token. */
        return String(this.t(label)).replace(/__TIME__/g, window.theme.displayTime(boundary));
      },
    }));
  });
})();
